/**
 * Live camera views of the Bay, from two operators.
 *
 * The Golden Gate National Parks Conservancy points two cameras at Crissy Field
 * and publishes them as iframe embeds on its own webcam page; those are used as
 * given. Everything else is a Caltrans District 4 traffic camera, published
 * openly by the state with a public JSON index and no referrer or key check,
 * chosen for looking at water rather than at traffic.
 *
 * Private webcams — the hang-gliding club at Fort Funston, restaurant cameras,
 * the aggregator sites — are deliberately not hotlinked. They cost someone else
 * bandwidth and carry no licence to embed. They belong in a link list.
 */
export interface BayCamera {
  id: string;
  name: string;
  /** What water it actually shows, since the Caltrans names are route-centric. */
  view: string;
  lat: number;
  lon: number;
  /** A still frame we refresh ourselves. */
  imageUrl?: string;
  /** A live embed published by the operator, used as-is. */
  embedUrl?: string;
  /** Whose camera it is, shown in the panel. */
  credit: string;
  /** Aspect ratio of the frame, so the panel reserves the right space. */
  aspect?: string;
  /**
   * Native frame size. Caps how far the expanded view will scale — Caltrans
   * publishes 320x260, so blowing it up much further only adds blur.
   */
  native: [number, number];
}

const CALTRANS = 'https://cwwp2.dot.ca.gov/data/d4/cctv/image';
const GGNPC = 'https://parksconservancy.siteproxy.net';

const CALTRANS_CREDIT = 'Caltrans District 4';
const GGNPC_CREDIT = 'Golden Gate National Parks Conservancy';

export const CAMERAS: BayCamera[] = [
  /*
   * Crissy Field first: East Beach is the windsurf and kite launch, and the one
   * place this site has never had wind data for — the free feeds stop at Fort
   * Point, half a mile west. A live picture is the next best thing.
   *
   * These use the iframe the Conservancy publishes on its own webcam page rather
   * than the MJPEG behind it, which is session-scoped and would be going around
   * the embed they actually offer.
   */
  {
    id: 'crissy-east',
    name: 'Crissy Field, East Beach',
    view: 'The windsurf and kite launch, looking at the Bay',
    lat: 37.8042, lon: -122.4523,
    embedUrl: `${GGNPC}/East%20Beach%20Webcam/ctl.html`,
    credit: GGNPC_CREDIT,
    aspect: '4 / 3',
    // Largest MJPEG the operator advertises for this camera.
    native: [640, 480],
  },
  {
    id: 'crissy-beach',
    name: 'Crissy Beach',
    view: 'Crissy shoreline toward the Golden Gate',
    lat: 37.8055, lon: -122.46,
    embedUrl: `${GGNPC}/Crissy%20Beach/ctl.html`,
    credit: GGNPC_CREDIT,
    aspect: '4 / 3',
    native: [1280, 960],
  },
  {
    id: 'tvd32',
    name: 'Bay Bridge, SAS tower',
    view: 'Central Bay, east span',
    lat: 37.81526, lon: -122.35855,
    imageUrl: `${CALTRANS}/tvd32i80baybridgesastowereast/tvd32i80baybridgesastowereast.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tvd36',
    name: 'Bay Bridge, incline',
    view: 'Central Bay toward Yerba Buena',
    lat: 37.82221, lon: -122.3252,
    imageUrl: `${CALTRANS}/tvd36i80sfobbatincline/tvd36i80sfobbatincline.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tve70',
    name: 'Bowers Vista Point',
    view: 'Golden Gate from the Marin side',
    lat: 37.83313, lon: -122.48142,
    imageUrl: `${CALTRANS}/tve70us101atbowersvistapoint/tve70us101atbowersvistapoint.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tve73',
    name: 'Spencer Avenue',
    view: 'Sausalito and the north Gate approach',
    lat: 37.85081, lon: -122.48975,
    imageUrl: `${CALTRANS}/tve73us101spenceravenue/tve73us101spenceravenue.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tvr46',
    name: 'Richmond, Marine Street',
    view: 'Richmond waterfront and San Pablo Bay',
    lat: 37.930825, lon: -122.391148,
    imageUrl: `${CALTRANS}/tvr46i580atmarinest/tvr46i580atmarinest.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tve02',
    name: 'San Mateo Bridge, incline',
    view: 'South Bay',
    lat: 37.572652, lon: -122.263271,
    imageUrl: `${CALTRANS}/tve02sr92sanmateobridgeincline/tve02sr92sanmateobridgeincline.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tv972',
    name: 'Carquinez Bridge',
    view: 'Carquinez Strait',
    lat: 38.05044, lon: -122.231204,
    imageUrl: `${CALTRANS}/tv972i80justwestofcarquinezbrdg/tv972i80justwestofcarquinezbrdg.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
  {
    id: 'tv792',
    name: 'Benicia-Martinez Skyway',
    view: 'Suisun Bay',
    lat: 38.040283, lon: -122.119568,
    imageUrl: `${CALTRANS}/tv792i680beniciamartinezskyway/tv792i680beniciamartinezskyway.jpg`,
    credit: CALTRANS_CREDIT,
    native: [320, 260],
  },
];

export function cameraById(id: string): BayCamera | undefined {
  return CAMERAS.find((c) => c.id === id);
}

/**
 * Caltrans overwrites each image in place roughly once a minute, so a cache
 * buster is what makes a refresh actually fetch anything.
 */
export function cameraSrc(camera: BayCamera, at: Date = new Date()): string {
  return `${camera.imageUrl}?t=${Math.floor(at.getTime() / 1000)}`;
}

/** Other Bay cameras worth knowing about, linked rather than embedded. */
export const CAMERA_LINKS = [
  {
    name: 'Golden Gate National Parks',
    href: 'https://www.parksconservancy.org/parks/park-web-cams',
    note: 'Muir Beach, Fort Funston, Mt Tamalpais',
  },
  {
    name: 'sfcam.live',
    href: 'https://sfcam.live/',
    note: 'Aggregator covering the city and the coast',
  },
];
